import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, NumberInput, Switch, Textarea, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {useServiceEventTicketPolicyForm} from "./useServiceEventTicketPolicyForm";
import {
  CompanyEntity,
  FreightTypeEntity,
  Quantity,
  ServiceEventType,
  ServiceTypeEntity,
  toServiceEventType,
  toTransportCategory,
  TransportCategory
} from "@ots-frontend/ots-data";
import {ServiceEventTypeSelect} from "../../select/entity/ServiceEventTypeSelect/ServiceEventTypeSelect";
import {RefServiceStatusFacetSelect} from "../../select/entity/RefServiceStatusFacetSelect/RefServiceStatusFacetSelect";
import {RefServiceStatusSelect} from "../../select/entity/RefServiceStatusSelect/RefServiceStatusSelect";
import {ServiceTypeSelect} from "../../select/entity/ServiceTypeSelect/ServiceTypeSelect";
import {TransportCategorySelect} from "../../select/entity/TransportCategorySelect/TransportCategorySelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import { CompanySelect } from "../../select/entity/CompanySelect/CompanySelect";

export interface ServiceEventTicketPolicyFormProps {
  options?: ServiceEventTicketPolicyFormOptions;
  defaultValues?: ServiceEventTicketPolicyFormDefaultValues;
  onSubmit?: (values: ServiceEventTicketPolicyFormValues) => void;
  submitting?: boolean;
}

export interface ServiceEventTicketPolicyFormOptions {
  regionId?: string;
}

export interface ServiceEventTicketPolicyFormValues {
  name: string;
  description?: string;
  eventType: ServiceEventType;
  company: CompanyEntity | string;
  serviceType: ServiceTypeEntity | string;
  triggerFacet: string;
  triggerFromStatus?: string;
  triggerToStatus: string;
  minSeverity?: number;
  category: TransportCategory;
  freightType: FreightTypeEntity | string;
  defaultQuantity?: Partial<Quantity>;
  priority: number
  doNotUse?: boolean
}

export interface ServiceEventTicketPolicyFormDefaultValues extends Partial<ServiceEventTicketPolicyFormValues> {

}

export interface ServiceEventTicketPolicyFormRawValues {
  name?: string;
  description?: string;
  eventType?: string;
  company?: string;
  serviceType?: string;
  triggerFacet?: string;
  triggerFromStatus?: string;
  triggerToStatus?: string;
  minSeverity?: number;
  category?: string;
  freightType?: string;
  defaultQuantityUom?: string;
  defaultQuantity?: number;
  priority?: number
  doNotUse?: boolean
}

export type ServiceEventTicketPolicyFormType = UseFormReturnType<ServiceEventTicketPolicyFormRawValues, (values: ServiceEventTicketPolicyFormRawValues) => ServiceEventTicketPolicyFormRawValues>

export const ServiceEventTicketPolicyForm = (props: ServiceEventTicketPolicyFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useServiceEventTicketPolicyForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {

    if (onSubmit) {
      onSubmit({
        name: values.name!,
        description: values.description,
        eventType: toServiceEventType(values.eventType!),
        company: values.company!,
        serviceType: values.serviceType!,
        triggerFacet: values.triggerFacet!,
        triggerFromStatus: values.triggerFromStatus,
        triggerToStatus: values.triggerToStatus!,
        minSeverity: values.minSeverity,
        category: toTransportCategory(values.category!),
        freightType: values.freightType!,
        defaultQuantity: {
          number: values.defaultQuantity,
          units: values.defaultQuantityUom,
        },
        priority: values.priority!,
        doNotUse: values.doNotUse,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.name", "Name")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.name.placeholder", "Name")}
            withAsterisk
            required
            {...form.getInputProps("name")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.desc", "Description")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.desc.placeholder", "Description")}
            {...form.getInputProps("description")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <CompanySelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.company", "Company")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.company.placeholder", "Company")}
            selector={{
              regionId: options?.regionId,
            }}
            clearable
            withAsterisk
            required
            {...form.getInputProps("company")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <ServiceEventTypeSelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.eventtype", "Event Type")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.eventtype.placeholder", "Event Type")}
            clearable
            withAsterisk
            required
            {...form.getInputProps("eventType")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <ServiceTypeSelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.servicetype", "Service Type")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.servicetype.placeholder", "Service Type")}
            selector={{
              regionId: options?.regionId,
            }}
            clearable
            withAsterisk
            required
            {...form.getInputProps("serviceType")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <RefServiceStatusFacetSelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.facet", "Trigger Facet")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.facet.placeholder", "Trigger Facet")}
            clearable
            withAsterisk
            required
            {...form.getInputProps("triggerFacet")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <RefServiceStatusSelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.fromstatus", "Trigger From Status")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.fromstatus.placeholder", "Trigger From Status")}
            clearable
            {...form.getInputProps("triggerFromStatus")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <RefServiceStatusSelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.tostatus", "Trigger To Status")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.tostatus.placeholder", "Trigger To Status")}
            clearable
            withAsterisk
            required
            {...form.getInputProps("triggerToStatus")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <NumberInput
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.minseverity", "Min. Severity")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.minseverity.placeholder", "Min. Severity")}
            step={1}
            min={0}
            max={5}
            {...form.getInputProps("minSeverity")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TransportCategorySelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.category", "Category")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.category.placeholder", "Category")}
            clearable
            withAsterisk
            required
            {...form.getInputProps("category")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <FreightTypeSelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.freighttype", "Freight Type")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.freighttype.placeholder", "Freight Type")}
            selector={{
              regionId: options?.regionId,
            }}
            clearable
            withAsterisk
            required
            {...form.getInputProps("freightType")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.quantityuom", "Default Quantity")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.quantityuom.placeholder", "Default Quantity")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            {...form.getInputProps("defaultQuantity")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <UnitOfMeasureSelect
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.quantityuom", "Units")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.quantityuom.placeholder", "Units")}
            clearable
            {...form.getInputProps("defaultQuantityUom")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <NumberInput
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.priority", "Priority")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.priority.placeholder", "Priority")}
            precision={0}
            withAsterisk
            required
            {...form.getInputProps("priority")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Switch
            size="xs"
            label={t("common.entity.serviceeventticketpolicy.form.field.donotuse", "Do Not Use")}
            placeholder={t<string>("common.entity.serviceeventticketpolicy.form.field.donotuse", "Do Not Use")}
            {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
        </Grid.Col>
      </Grid>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};
